import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./css/styles.css";
import "./fontello/css/fontello.css"
import "./fontello/css/animation.css"

import { Link, Route, Routes, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";

import Admin from "./components/Admin";
import AlbumForm from "./components/AlbumForm";
import AlbumManagement from "./components/AlbumManagement";
import AuthService from "./services/auth.service";
import BoardAdmin from "./components/BoardAdmin";
import BoardModerator from "./components/BoardModerator";
import BoardUser from "./components/BoardUser";
import Cart from "./components/Cart";
import Catalog from "./components/Catalog";
import Checkout from "./components/Checkout";
import CreateCatalog from "./components/CreateCatalog";
import CreateSubCatalog from "./components/CreateSubCatalog";
import EventBus from "./common/EventBus";
import Footer from "./components/Footer";
import Gallery from "./components/Gallery";
import Home from "./components/Home";
import LanguageSwitcher from "./components/LanguageSwitcher";
import Login from "./components/Login";
import News from "./components/News";
import NewsDetail from "./components/NewsDetail";
import NewsManagement from "./components/NewsManagement";
import OrderDetail from "./components/OrderDetail";
import OrderManagement from "./components/OrderManagement";
import Orders from "./components/Orders";
import ProductDetail from "./components/ProductDetail";
import Products from "./components/Products";
import Profile from "./components/Profile";
import Register from "./components/Register";
import RequireRole from "./components/RequireRole";
import RouteSeo from "./seo/RouteSeo";
import Upload from "./components/Upload";
import UserManagement from "./components/UserManagement";
import setupInterceptors from "./common/http/setup-interceptors";
import { setupLoadingTracker } from "./common/http/loading-tracker";
import GlobalLoadingBar from "./components/GlobalLoadingBar";
import toast from "react-hot-toast";
import { useCart } from "./context/CartContext";
import { useTranslation } from "react-i18next";

const MOD_OR_ADMIN = ["ROLE_MODERATOR", "ROLE_ADMIN"];

setupInterceptors();
setupLoadingTracker();

const App = () => {
  const { t } = useTranslation();
  const { itemCount } = useCart();
  const [currentUser, setCurrentUser] = useState(undefined);

  const canManageCatalog =
    currentUser &&
    (currentUser.roles || []).some((r) => MOD_OR_ADMIN.includes(r));

  const navigate = useNavigate();

  const logOut = () => {
    AuthService.logout();
    setCurrentUser(null);
  };

  // Initialize auth + logout listener.
  useEffect(() => {
    const user = AuthService.getCurrentUser(); // null if token expired
    setCurrentUser(user);

    const onLogout = (detail) => {
      logOut();
      if (detail?.reason === "expired") {
        toast.error(t("auth.sessionExpired", "Your session has expired. Please log in again."), { id: "session-expired" });
        navigate("/", { replace: true });
      }
    };
    EventBus.on("logout", onLogout);

    // Periodically check token expiry so the UI updates even without API calls.
    const timer = setInterval(() => {
      const stored = localStorage.getItem("user");
      if (stored && !AuthService.getCurrentUser()) {
        EventBus.dispatch("logout", { reason: "expired" });
      }
    }, 60 * 1000);

    // Keep tabs in sync (logout/login in another tab).
    const onStorage = (e) => {
      if (e.key === "user" || e.key === null) setCurrentUser(AuthService.getCurrentUser());
    };
    window.addEventListener("storage", onStorage);

    return () => {
      EventBus.remove("logout", onLogout);
      clearInterval(timer);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app-shell">
      <GlobalLoadingBar />
      <nav className="navbar navbar-expand-lg bg-body-tertiary">
        <div className="container-fluid">
          <div className="footer__logo d-flex align-items-center">
            <img src="/assets/img/logo.png" alt="Logo" width="50" height="50" />
            <div className="logo-text ms-2">
              <div className=''>CÔNG TY THƯƠNG MẠI VÀ SẢN XUẤT</div> <br /> <span className="fw-bold">TINH ANH</span>
              {/* <br /><small>Since 2008</small> */}
            </div>
          </div>
          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto gap-2">
              <li className="nav-item">
                <Link className="nav-link active" aria-current="page" to="/">{t("nav.home")}</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/#gioi-thieu">{t("nav.about")}</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/catalog">{t("nav.products")}</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/news">{t("nav.news")}</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/gallery">{t("nav.gallery")}</Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/#doi-tac">{t("nav.contact")}</Link>
              </li>
              {currentUser && (
                <li className="nav-item">
                  <Link className="nav-link" to="/orders">{t("nav.myOrders")}</Link>
                </li>
              )}
              {canManageCatalog && (
                <li className="nav-item">
                  <Link className="nav-link" to="/admin">{t("nav.admin")}</Link>
                </li>
              )}
              {currentUser && (
                <li className="nav-item">
                  <Link className="nav-link position-relative" to="/cart">
                    {t("nav.cart")}<i className='icon-basket'></i>
                    {itemCount > 0 && (
                      <span className="badge rounded-pill bg-danger ms-1">{itemCount}</span>
                    )}
                  </Link>
                </li>
              )}
              {/* <li className="nav-item d-flex align-items-center ms-lg-2">
                <LanguageSwitcher />
              </li> */}
              {currentUser ? (
                <>
                  <li className="nav-item dropdown">
                    <Link className="nav-link dropdown-toggle" to="/profile" data-bs-toggle="dropdown">{currentUser.username}</Link>
                    <ul className="dropdown-menu dropdown-menu-end px-2">
                      <li><Link className="nav-link" to="/profile">{t("nav.profile")}</Link></li>
                      <li><Link className="nav-link" to="/" onClick={logOut}>{t("nav.logout")}</Link></li>
                    </ul>
                  </li>
                </>
              ) : (
                <>
                  <li className="nav-item">
                    <Link className="nav-link" to="/login">{t("nav.login")}</Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link" to="/register">{t("nav.register")}</Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>


      <main className="app-main">
      <RouteSeo />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/news" element={<News />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/gallery/:id" element={<Gallery />} />
        <Route
          path="/manage-albums"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <AlbumManagement />
            </RequireRole>
          }
        />
        <Route
          path="/manage-albums/new"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <AlbumForm />
            </RequireRole>
          }
        />
        <Route
          path="/manage-albums/:id"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <AlbumForm />
            </RequireRole>
          }
        />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route
          path="/manage-news"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <NewsManagement />
            </RequireRole>
          }
        />
        <Route
          path="/manage-users"
          element={
            <RequireRole allowedRoles={["ROLE_ADMIN"]}>
              <UserManagement />
            </RequireRole>
          }
        />
        <Route path="/profile" element={<Profile />} />
        <Route path="/user" element={<BoardUser />} />
        <Route path="/mod" element={<BoardModerator />} />
        <Route path="/admin1" element={<BoardAdmin />} />
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route
          path="/manage-orders"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <OrderManagement />
            </RequireRole>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <Admin />
            </RequireRole>
          }
        />
        <Route
          path="/create-catalog"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <CreateCatalog />
            </RequireRole>
          }
        />
        <Route
          path="/create-sub-catalog"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <CreateSubCatalog />
            </RequireRole>
          }
        />
        <Route
          path="/products"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <Products />
            </RequireRole>
          }
        />
        <Route
          path="/upload"
          element={
            <RequireRole allowedRoles={MOD_OR_ADMIN}>
              <Upload />
            </RequireRole>
          }
        />
      </Routes>
      </main>

      <Footer />

    </div >
  );
};

export default App;